import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { OpsStages, OpsStagesColor } from "components/salesCrm/OpsStage";
import { Form, Formik, FormikValues } from "formik";
import { opsBulkActionSchema } from "formValidations/opsBulkActionSchema";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { useUpdateBulkCompanyGroupMutation } from "store/apis/salesCrm";
import { BackendError } from "types/utils/error";
import { OpsBulkActionConfig, OpsBulkActionType } from "./OpsBulkAction";
import { useRoleBasedView } from "hooks/useRoleBasedView";

type BulkActionModalProps = {
  isOpen: boolean;
  close: () => void;
  selectedRows: string[];
  actionType: OpsBulkActionType | null;
  resetSelection: () => void;
  actionDetails: OpsBulkActionConfig | null;
  setBulkActionType: (val: OpsBulkActionType | null) => void;
};

export const BulkActionModal = ({
  isOpen,
  close,
  selectedRows,
  actionType,
  resetSelection,
  actionDetails,
  setBulkActionType,
}: BulkActionModalProps) => {
  const [updateBulkCompanyGroup, { isLoading: isUpdating }] =
    useUpdateBulkCompanyGroupMutation();
  const [options, setOptions] = useState<
    Array<{ value: string; label: string | React.JSX.Element }>
  >([]);
  const { isAdmin } = useRoleBasedView();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const {
    title,
    toastMessage,
    toastTitle,
    placeholder,
    label,
    loadingMessage,
  } = actionDetails || {};

  const { data: agents, isLoading } = useGetAssignAgentsQuery(
    {
      agentType: actionType === "agent" ? "CALL_EXECUTIVE" : "SALES_EXECUTIVE",
    },
    {
      skip:
        !isAdmin || (actionType !== "sales_person" && actionType !== "agent"),
    }
  );

  useEffect(() => {
    switch (actionType) {
      case "sales_person":
        if (agents) {
          setOptions(
            agents.map((person) => ({
              value: person.profile_id,
              label: person.name,
            }))
          );
        }
        break;
      case "agent":
        if (agents) {
          setOptions(
            agents.map((agent) => ({
              value: agent.profile_id,
              label: agent.name,
            }))
          );
        }
        break;
      case "ops_stage":
        const options = OpsStages.map((stage: any) => {
          const [stageName, stageStatus] = stage;

          return {
            label: (
              <div key={stageStatus} className="t-flex t-gap-2 t-items-center">
                <div
                  className={classNames(
                    "t-inline-block t-h-[15px] t-w-[15px] t-rounded-[50%]",
                    // @ts-ignore
                    OpsStagesColor?.[stageName]?.[1]
                  )}
                />
                <div>{stageStatus}</div>
              </div>
            ),
            value: stageName,
          };
        });
        setOptions(options);
        break;

      default:
        setOptions([]);
    }
  }, [actionType, agents]);

  const handleUpdate = async (values: FormikValues) => {
    const loadingtoastId = loadingToast({
      title: "Updating",
      message: loadingMessage,
    });
    try {
      if (actionType) {
        close();
        await updateBulkCompanyGroup({
          payload: {
            group_ids: selectedRows,
            update_key: actionType,
            update_value: values.value,
          },
        }).unwrap();
        successToast({
          title: toastTitle,
          message: `${toastMessage} ${selectedRows.length} clients`,
        });
        resetSelection();
        setBulkActionType(null);
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>

          <Formik
            initialValues={{ value: "" }}
            onSubmit={handleUpdate}
            enableReinitialize
            validationSchema={opsBulkActionSchema}
          >
            {({ handleSubmit }) => {
              return (
                <Form onSubmit={handleSubmit} className="t-m-0">
                  <Modal.Body>
                    {isLoading ? (
                      <Loader size="small" />
                    ) : (
                      <Combobox
                        withForm
                        name="value"
                        label={label}
                        required
                        menuPortalTarget={document.body}
                        options={options}
                        placeholder={placeholder}
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            width: "300px",
                          }),
                        }}
                      />
                    )}
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button type="button">Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      type="submit"
                      isLoading={isUpdating}
                    >
                      Assign all ({selectedRows.length})
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              );
            }}
          </Formik>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
